/* istanbul ignore file */
import React, { useEffect, useState } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import {
  TextField,
  Grid,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
} from '@material-ui/core';
import { get, isEmpty, isNil } from 'lodash';
import { toast } from 'react-toastify';
import { INJURY_POST_DECEASED } from 'containers/PreOpeningPage/constants';
import { translate } from 'containers/services/translate';
import { Config, Listings, SagisConfig } from 'containers/AppContainer/typings';
import { BUTTON_COLORS } from 'constants/design';
import {
  getLinkWithRecepientForMaterialRisk,
  paymentMethod,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/utils';
import {
  getCities,
  getCountriesWithCities,
} from 'containers/RecapManagerPage/utils';
import messages from 'containers/TransactionManagerTimeLine/OfferRegulation/messages';
import { REGULATIONS } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/data-structure';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import {
  regulationToAdd,
  getTransactionPaymentNature,
  getTransactionPaymentChannel,
  filterBeneficiariesWithRegulations,
  formatRecapRegulation,
} from 'containers/TransactionManagerTimeLine/OfferRegulation/utils';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import { RECIPIENT } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/constants';
import { TransactionType } from 'typings/globalTypes';

export interface RegulationFormProps {
  intl: IntlShape;
  inputLabelProps?: boolean;
  listings: Listings;
  isReadOnly: boolean;
  offerInfo: any;
  sagisClaim: any;
  victimInfo: any;
  responsabilitySharedRate: any;
  requestCreateCorporealRegulation: Function;
  regulationInfo: any;
  setRegulationInfo: Function;
  requestGetRegulations: Function;
  requestSagisClaimInfo: Function;
  transactionInfo: TransactionType;
  corpoRegulations: any;
  corpoRegulation: any;
}

const RegulationForm: React.FC<RegulationFormProps> = (props): JSX.Element => {
  const [regulationInfoForm, setRegulationInfoForm] = useState(
    props.regulationInfo as any,
  );
  const [displayAddButton, setDisplayAddButton] = useState(false);
  const [cities, setCities] = useState(
    getCities(
      get(props.listings, 'villes', []),
      get(props.regulationInfo, 'countryCode'),
    ) as any,
  );
  useEffect(() => {
    if (!isEmpty(props.regulationInfo) && props.isReadOnly) {
      setRegulationInfoForm({ ...props.regulationInfo });
      setCities(
        getCities(
          get(props.listings, 'villes', []),
          get(props.regulationInfo, 'countryCode'),
        ) as any,
      );
    }
  }, [
    props.regulationInfo,
    props.listings,
    props.sagisClaim,
    props.isReadOnly,
    props.corpoRegulation,
  ]);

  const handleChange = (
    event: React.ChangeEvent<{ name?: string | undefined; value: any }>,
  ) => {
    if (event.target.name === REGULATIONS.COUNTRY_CODE) {
      setRegulationInfoForm({
        ...regulationInfoForm,
        [REGULATIONS.COUNTRY_CODE]: event.target.value,
      });

      setCities(
        getCities(get(props.listings, 'villes', []), event.target.value) as any,
      );
    } else {
      setRegulationInfoForm({
        ...regulationInfoForm,
        [event.target.name as any]: event.target.value,
      });
    }
    props.setRegulationInfo(regulationInfoForm);
  };
  const handleChangeAutoComplete = (
    event: React.ChangeEvent<{ name?: string | undefined; value: any }>,
    newValue: any,
  ) => {
    if (newValue) {
      setRegulationInfoForm({
        ...regulationInfoForm,
        [REGULATIONS.TOWN_CODE]: newValue.ItemKey,
      });
      props.setRegulationInfo({
        ...regulationInfoForm,
        [REGULATIONS.TOWN_CODE]: newValue.ItemKey,
      });
    } else {
      setRegulationInfoForm({
        ...regulationInfoForm,
        [REGULATIONS.TOWN_CODE]: null,
      });
      props.setRegulationInfo({
        ...regulationInfoForm,
        [REGULATIONS.TOWN_CODE]: null,
      });
    }
  };
  const countries: SagisConfig[] = getCountriesWithCities(
    get(props.listings, 'country', []),
    get(props.listings, 'villes', []),
  );
  const filterOptions = createFilterOptions({
    matchFrom: 'start',
    stringify: (option: SagisConfig) => option?.ItemDescription,
  });
  const successCallBack = () => {
    setDisplayAddButton(true);
    toast.success(translate(messages.successToastCreateRegulation, props));
    props.requestSagisClaimInfo(get(props.sagisClaim, 'code', ''));
    props.requestGetRegulations(get(props.victimInfo, 'claimId'));
  };
  const handleValidate = () => {
    const regulation = regulationToAdd(
      regulationInfoForm,
      props.sagisClaim,
      props.responsabilitySharedRate,
      props.victimInfo,
      props.listings,
      get(props.offerInfo, 'rcAmount'),
      props.transactionInfo,
      filterBeneficiariesWithRegulations(
        get(props.sagisClaim, 'regulations', []),
        props.corpoRegulations.map((el: any) => el.sagisRegulationId),
        get(props.transactionInfo, 'victim.victimKey', {}),
        get(props.victimInfo, 'beneficiaries', []),
      ),
    );

    if (!isEmpty(regulation)) {
      props.requestCreateCorporealRegulation(regulation, successCallBack);
    }
  };

  const handleChangeSelect = (event: React.ChangeEvent<{}>, value: any[]) => {
    setRegulationInfoForm({
      ...regulationInfoForm,
      beneficiaries: value,
    });
    props.setRegulationInfo({
      ...regulationInfoForm,
      beneficiaries: value,
    });
  };

  return (
    <div className="victim-container">
      <Grid container direction="row" spacing={2}>
        {get(props.victimInfo, 'injuryPostKey') === INJURY_POST_DECEASED &&
          !isEmpty(
            get(props.victimInfo, 'beneficiaries', []).filter(
              (el: any) => !isNil(el.nextOfKinKey),
            ),
          ) && (
            <Grid item xs={4}>
              <Autocomplete
                className="lawyer-item"
                multiple
                id="assignedLawyers"
                options={filterBeneficiariesWithRegulations(
                  get(props.sagisClaim, 'regulations', []),
                  props.corpoRegulations.map((el: any) => el.sagisRegulationId),
                  get(props.transactionInfo, 'victim.victimKey', {}),
                  get(props.victimInfo, 'beneficiaries', []).filter(
                    (el: any) => !isNil(el.nextOfKinKey),
                  ),
                )}
                getOptionLabel={(option: any) =>
                  `${option?.lastName} ${option?.firstName}`
                }
                value={get(regulationInfoForm, 'beneficiaries', [])}
                onChange={handleChangeSelect}
                disabled={props.isReadOnly}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={translate(messages.beneficiaryTitle, props)}
                  />
                )}
              />
            </Grid>
          )}
        <Grid item xs={4}>
          <FormControl fullWidth>
            <InputLabel>
              {translate(messages.regulationPhase, props)}
            </InputLabel>
            <Select
              onChange={handleChange}
              data-testId={REGULATIONS.PAYMENT_NATURE}
              name={REGULATIONS.PAYMENT_NATURE}
              id={REGULATIONS.PAYMENT_NATURE}
              value={get(regulationInfoForm, [REGULATIONS.PAYMENT_NATURE], '')}
              error={false}
              readOnly={props.isReadOnly}
            >
              {getTransactionPaymentNature(props.listings).map(
                (el: SagisConfig) => (
                  <MenuItem
                    data-testId={el.ItemKey}
                    id={el.ItemKey}
                    key={el.ItemKey}
                    value={el.ItemKey}
                  >
                    {el.ItemDescription}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <InputLabel>{translate(messages.paymentMethod, props)}</InputLabel>

            <Select
              onChange={handleChange}
              data-testid={REGULATIONS.PAYMENT_METHOD}
              name={REGULATIONS.PAYMENT_METHOD}
              id={REGULATIONS.PAYMENT_METHOD}
              value={get(regulationInfoForm, REGULATIONS.PAYMENT_METHOD, '')}
              error={false}
              readOnly={props.isReadOnly}
            >
              {paymentMethod(get(props.listings, 'PaymentMethod', [])).map(
                (el: SagisConfig) => (
                  <MenuItem
                    data-testid={el.ItemKey}
                    id={el.ItemKey}
                    key={el.ItemKey}
                    value={el.ItemKey}
                  >
                    {el.ItemDescription}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <InputLabel id="idd-label-id">
              {translate(messages.recipient, props)}
            </InputLabel>
            <Select
              onChange={handleChange}
              data-testId={REGULATIONS.BENEFICIARY_RECIPIENT}
              name={REGULATIONS.BENEFICIARY_RECIPIENT}
              id={REGULATIONS.BENEFICIARY_RECIPIENT}
              value={get(
                regulationInfoForm,
                [REGULATIONS.BENEFICIARY_RECIPIENT],
                '',
              )}
              error={false}
              readOnly={props.isReadOnly}
            >
              {get(props.listings, 'regulationRecipient', []).map(
                (el: Config) => (
                  <MenuItem data-testId={el.title} key={el.id} value={el.title}>
                    {el.title}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </Grid>
        {get(regulationInfoForm, [REGULATIONS.BENEFICIARY_RECIPIENT]) ===
          RECIPIENT.BNEJ && (
          <Grid item xs={4}>
            <FormControl fullWidth>
              <InputLabel id="idd-label-id">
                {translate(messages.bnejCity, props)}
              </InputLabel>
              <Select
                onChange={handleChange}
                data-testid={REGULATIONS.CITY_CODE}
                name={REGULATIONS.CITY_CODE}
                id={REGULATIONS.CITY_CODE}
                value={get(regulationInfoForm, [REGULATIONS.CITY_CODE], '')}
                error={false}
                readOnly={props.isReadOnly}
              >
                {get(props.listings, 'EncryptDocumentCity', []).map(
                  (el: SagisConfig) => (
                    <MenuItem
                      data-testid={el.ItemDescription}
                      key={el.ItemKey}
                      value={el.ItemKey}
                    >
                      {el.ItemDescription}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={4}>
          <TextField
            fullWidth
            label={translate(messages.beneficiaryReference, props)}
            onChange={handleChange}
            id={REGULATIONS.BENEFICIARY_REFERENCE}
            data-testid={REGULATIONS.BENEFICIARY_REFERENCE}
            name={REGULATIONS.BENEFICIARY_REFERENCE}
            value={get(
              regulationInfoForm,
              [REGULATIONS.BENEFICIARY_REFERENCE],
              '',
            )}
            error={false}
            disabled={props.isReadOnly}
          />
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <InputLabel>{translate(messages.paymentChannel, props)}</InputLabel>

            <Select
              onChange={handleChange}
              data-testid={REGULATIONS.PAYMENT_CHANNEL}
              name={REGULATIONS.PAYMENT_CHANNEL}
              id={REGULATIONS.PAYMENT_CHANNEL}
              value={get(regulationInfoForm, REGULATIONS.PAYMENT_CHANNEL, '')}
              error={false}
              readOnly={props.isReadOnly}
            >
              {getTransactionPaymentChannel(props.listings).map(
                (el: SagisConfig) => (
                  <MenuItem
                    data-testid={el.ItemKey}
                    id={el.ItemKey}
                    key={el.ItemKey}
                    value={el.ItemKey}
                  >
                    {el.ItemDescription}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={4}>
          <FormControl fullWidth>
            <InputLabel id="idd-label-id">
              {translate(messages.linkToBeneficiary, props)}
            </InputLabel>
            <Select
              onChange={handleChange}
              data-testid={REGULATIONS.LINK_TO_BENEFICIARY}
              name={REGULATIONS.LINK_TO_BENEFICIARY}
              id={REGULATIONS.LINK_TO_BENEFICIARY}
              value={get(
                regulationInfoForm,
                REGULATIONS.LINK_TO_BENEFICIARY,
                '',
              )}
              error={false}
              readOnly={props.isReadOnly}
            >
              {getLinkWithRecepientForMaterialRisk(
                get(props.listings, 'LinesOfRecipt', []),
              ).map((el: SagisConfig) => (
                <MenuItem
                  data-testid={el.ItemKey}
                  key={el.ItemKey}
                  value={el.ItemKey}
                >
                  {el.ItemDescription}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <InputLabel>{translate({ ...messages.country }, props)}</InputLabel>
            <Select
              onChange={handleChange}
              name={REGULATIONS.COUNTRY_CODE}
              value={get(regulationInfoForm, [REGULATIONS.COUNTRY_CODE], '')}
              fullWidth
              error={false}
              readOnly={props.isReadOnly}
            >
              {countries.map((country: SagisConfig) => (
                <MenuItem value={country.ItemKey} key={country.ItemKey}>
                  {country.ItemDescription}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <Autocomplete
            options={cities as any}
            value={
              cities.find(
                (el: any) =>
                  el.ItemKey ===
                  get(regulationInfoForm, [REGULATIONS.TOWN_CODE]),
              ) || null
            }
            id={REGULATIONS.TOWN_CODE}
            getOptionLabel={(city: any) => get(city, 'ItemDescription', null)}
            autoSelect={true}
            disabled={props.isReadOnly}
            onChange={handleChangeAutoComplete as any}
            filterOptions={filterOptions}
            renderInput={params => (
              <TextField
                {...params}
                name={REGULATIONS.TOWN_CODE}
                label={translate({ ...messages.town }, props)}
                error={false}
              />
            )}
            fullWidth
          />
        </Grid>
        <Grid item xs={4}>
          {!props.isReadOnly &&
            isEmpty(
              formatRecapRegulation(
                get(props.victimInfo, 'victimKey'),
                [get(props.corpoRegulation, 'sagisRegulationId')],
                get(props.sagisClaim, 'regulations'),
                props.listings,
                get(props.victimInfo, 'beneficiaries', []),
              ),
            ) &&
            isNil(
              get(
                formatRecapRegulation(
                  get(props.victimInfo, 'victimKey'),
                  [get(props.corpoRegulation, 'sagisRegulationId')],
                  get(props.sagisClaim, 'regulations'),
                  props.listings,
                  get(props.victimInfo, 'beneficiaries', []),
                ),
                'paymentId',
                null,
              ),
            ) &&
            !displayAddButton && (
              <PrimaryButton
                data-testid="validate"
                background={BUTTON_COLORS.PRIMARY_BLUE}
                border={BUTTON_COLORS.PRIMARY_BLUE}
                titlecolor={BUTTON_COLORS.BLUE_BUTTON_TITLE}
                title={translate(messages.validatedButtonTitle, props)}
                onClick={handleValidate}
              />
            )}
        </Grid>
      </Grid>
    </div>
  );
};
export default injectIntl(RegulationForm);
